import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomerOrderDrawer } from "@/components/CustomerOrderDrawer";
import { Button } from "@/components/ui/button";
import { fetchMyOrder, fetchMyOrderSummaries, formatOrderNumber, toOrderSummaryFromDetail } from "@/lib/ordersApi";
import { ORDER_ITEM_PREVIEW_LIMIT, ORDER_STATUS_COLORS, ORDER_STATUS_LABELS } from "@/lib/orderUtils";
import { cn } from "@/lib/utils";
import { useApp } from "@/store/useApp";
import { CalendarDays, ChevronRight, ClipboardList, Loader2, Lock, Package2, ShoppingBag } from "lucide-react";
import type { OrderDetail, OrderSummary } from "@/lib/types";

export default function Orders() {
  const navigate = useNavigate();
  const account = useApp((state) => state.account);
  const [orders, setOrders] = useState<OrderSummary[]>([]);
  const [loadState, setLoadState] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<OrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const validated = account?.status === "VALIDADO";

  const loadOrders = useCallback(async () => {
    setLoadState("loading");
    setLoadError(null);
    try {
      const summaries = await fetchMyOrderSummaries();
      setOrders(summaries);
      setLoadState("ready");
    } catch (error) {
      setOrders([]);
      setLoadState("error");
      setLoadError(error instanceof Error ? error.message : "Nao foi possivel carregar os pedidos");
    }
  }, []);

  useEffect(() => {
    void loadOrders();
  }, [loadOrders]);

  const openOrder = async (orderId: string) => {
    setDetailLoading(true);
    setSelectedOrder(null);
    try {
      const detail = await fetchMyOrder(orderId);
      setSelectedOrder(detail);
    } catch {
      setSelectedOrder(null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="container max-w-4xl space-y-6 py-8">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight">Meus Pedidos</h1>
        <p className="mt-1 text-muted-foreground">Histórico, status e edição rápida de pedidos anteriores.</p>
      </div>

      {loadState === "loading" && (
        <div className="surface-card p-12 text-center text-sm text-muted-foreground">
          <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
          Carregando pedidos...
        </div>
      )}

      {loadState === "error" && (
        <div className="surface-card p-12 text-center text-sm text-destructive">
          {loadError ?? "Nao foi possivel carregar os pedidos"}
        </div>
      )}

      {loadState !== "loading" && loadState !== "error" && orders.length === 0 && (
        <div className="surface-card p-12 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary">
            <ClipboardList className="h-6 w-6 text-muted-foreground" />
          </div>
          <h3 className="font-display text-lg font-semibold">Nenhum pedido por aqui ainda</h3>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
            {validated
              ? "Quando você fizer um pedido, ele aparece aqui com status, data e valor."
              : "Sua ficha comercial está em análise. Você pode montar pedidos no catálogo — eles ficam como cotação até a validação."}
          </p>
          <Button variant="hero" className="mt-5" onClick={() => navigate("/catalogo")}>
            Ir para o catálogo
          </Button>
        </div>
      )}

      {loadState !== "loading" && loadState !== "error" && orders.length > 0 && (
        <div className="space-y-2">
          {orders.map((order) => {
            const colors = ORDER_STATUS_COLORS[order.status];
            const shownItems = order.itemPreview.slice(0, ORDER_ITEM_PREVIEW_LIMIT);
            const remainingItems = Math.max(0, order.itemCount - shownItems.length);
            return (
              <button
                key={order.id}
                type="button"
                onClick={() => void openOrder(order.id)}
                className="flex w-full items-center gap-4 p-4 text-left transition-all hover:border-primary/30 hover:shadow-md surface-card"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gradient-primary">
                  <Package2 className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-display font-semibold">
                      {order.number != null ? formatOrderNumber(order.number) : new Date(order.createdAt).toLocaleDateString("pt-BR")}
                    </span>
                    <span className={cn("rounded-md px-2 py-0.5 text-xs font-medium", colors.bg, colors.text)}>
                      {ORDER_STATUS_LABELS[order.status]}
                    </span>
                    {order.number != null && (
                      <span className="text-xs text-muted-foreground">
                        {new Date(order.createdAt).toLocaleDateString("pt-BR")}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <ShoppingBag className="h-3 w-3" />
                      {order.itemCount} ite{order.itemCount === 1 ? "m" : "ns"}
                    </span>
                    {order.deliveryWindow && (
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="h-3 w-3" />
                        {order.deliveryWindow}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {shownItems.join(" · ")}
                    {remainingItems > 0 ? ` +${remainingItems}` : ""}
                  </p>
                </div>
                {validated && order.total !== undefined && order.total !== null ? (
                  <span className="font-display font-semibold tabular-nums">R$ {order.total.toFixed(2)}</span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                    <Lock className="h-3 w-3" />
                    sob aprovação
                  </span>
                )}
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </button>
            );
          })}
        </div>
      )}

      <CustomerOrderDrawer
        order={selectedOrder}
        loading={detailLoading}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedOrder(null);
            setDetailLoading(false);
          }
        }}
        onOrderUpdated={(updated) => {
          const summary = toOrderSummaryFromDetail(updated);
          setOrders((current) => current.map((order) => (order.id === updated.id ? summary : order)));
          setSelectedOrder(updated);
        }}
        onOrderDeleted={(orderId) => {
          setOrders((current) => current.filter((order) => order.id !== orderId));
          setSelectedOrder(null);
        }}
      />
    </div>
  );
}
